import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { ElementIdEnum } from '../../../enums/element-id-enum';
import { SnackbarSeverityEnum } from '../../../enums/snackbar-severity-enum';
import { IUserFileDB } from '../../../interfaces/file.interface';
import { useDeleteDocumentMutation } from '../../../store/apiSlice/userApi';
import { errorHelper } from '../../../utils/helper/error-helper';
import { validateFileSize } from '../../../utils/helper/file-validation-helper';
import StepForm from '../../Auth/VerifyAccount/StepForm';
import UploadIcon from '../../Icons/UploadIcon';
import DragAndDropBox from '../../MUIComponents/DragAndDropBox/DragAndDropBox';
import SnackbarCustom from '../../MUIComponents/SnackbarCustom';
import UploadDocumentFileList from './UploadDocumentFileList/UploadDocumentFileList';

export interface FileUploadFormInputs {
  documents: File[];
}

interface FileUploadFormProps {
  title: string | React.ReactNode;
  contentText: string | React.ReactNode;
  dragAndDropContentText: string | React.ReactNode;
  onSubmitFiles: SubmitHandler<any>;
  formData: Partial<FileUploadFormInputs>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  step: number;
  files: File[];
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
  uploadedFiles: IUserFileDB[];
  setUploadedFiles: React.Dispatch<React.SetStateAction<IUserFileDB[]>>;
  verifyFileHelper: (files: File) => boolean;
  isLoading: boolean;
  isError: boolean;
  error: any;
  responseData: any;
  maxFiles?: number;
  needBackButton?: boolean;
}

const UploadDocument: React.FC<FileUploadFormProps> = ({
  title,
  contentText,
  dragAndDropContentText,
  onSubmitFiles,
  formData: _formData,
  setStep,
  step,
  files,
  setFiles,
  uploadedFiles,
  setUploadedFiles,
  verifyFileHelper,
  isLoading,
  isError,
  error,
  responseData,
  maxFiles,
  needBackButton,
}) => {
  const [_dragOver, setDragOver] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] =
    useState<SnackbarSeverityEnum>(SnackbarSeverityEnum.ERROR);

  const [uploadProgress, setUploadProgress] = useState<number[]>([]);

  const [deleteDocument, { isLoading: isDeleting }] =
    useDeleteDocumentMutation();

  useEffect(() => {
    if (!isLoading) {
      setUploadProgress((prev) =>
        prev.map((value) => (value === -1 ? 100 : value))
      );
    }
  }, [isLoading]);

  useEffect(() => {
    if (responseData) {
      const newFiles = Array.isArray(responseData.data)
        ? responseData.data
        : responseData.data
          ? [responseData.data]
          : [];

      if (newFiles.length) {
        setUploadedFiles((prevState) => [...prevState, ...newFiles]);
        setUploadProgress((prevState) => prevState.map(() => 100));
      }
    }

    if (isError) {
      const errorMessage = errorHelper(error);
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity(SnackbarSeverityEnum.ERROR);
      setSnackbarOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responseData, isError, error]); // Intentionally omitting setUploadedFiles - prop from parent may not be memoized

  const handleFiles = async (newFiles: File[]) => {
    const validFiles = newFiles.filter((file) => verifyFileHelper(file));
    const invalidFiles = newFiles.filter((file) => !verifyFileHelper(file));

    if (validFiles.length > 5) {
      setSnackbarMessage('You can upload up to 5 files');
      setSnackbarSeverity(SnackbarSeverityEnum.ERROR);
      setSnackbarOpen(true);
      return;
    }

    if (invalidFiles.length > 0) {
      setSnackbarMessage(
        'Invalid file type. Only images (JPEG, PNG, JPG, WebP)'
      );
      setSnackbarSeverity(SnackbarSeverityEnum.ERROR);
      setSnackbarOpen(true);
      return;
    }

    const fileSizeValidation = validFiles.map((file) => validateFileSize(file));
    const oversizedFile = fileSizeValidation.find((result) => !result.valid);
    if (oversizedFile) {
      setSnackbarMessage(oversizedFile.error || 'File size exceeds the limit');
      setSnackbarSeverity(SnackbarSeverityEnum.ERROR);
      setSnackbarOpen(true);
      return;
    }

    if (maxFiles && files.length + validFiles.length > maxFiles) {
      setSnackbarMessage('You can upload up to 5 files');
      setSnackbarSeverity(SnackbarSeverityEnum.ERROR);
      setSnackbarOpen(true);
      return;
    }

    // Prevent adding duplicate files (same name, size, and lastModified)
    const existingKeys = new Set(
      files.map((file) => `${file.name}-${file.size}-${file.lastModified}`)
    );

    const uniqueValidFiles = validFiles.filter((file) => {
      const key = `${file.name}-${file.size}-${file.lastModified}`;
      if (existingKeys.has(key)) {
        return false;
      }
      existingKeys.add(key);
      return true;
    });

    if (uniqueValidFiles.length === 0) {
      setSnackbarMessage('File(s) are already added.');
      setSnackbarSeverity(SnackbarSeverityEnum.ERROR);
      setSnackbarOpen(true);
      return;
    }

    setFiles((prevState) => [...prevState, ...uniqueValidFiles]);
    const startIndex = uploadProgress.length;
    setUploadProgress((prevState) => [
      ...prevState,
      ...uniqueValidFiles.map(() => -1),
    ]);

    try {
      if (!isLoading) {
        setUploadProgress((prev) =>
          prev.map((value, index) =>
            index >= startIndex &&
            index < startIndex + uniqueValidFiles.length &&
            value === -1
              ? 100
              : value
          )
        );
      }
    } catch (uploadError) {
      const errorMessage = errorHelper(uploadError);
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity(SnackbarSeverityEnum.ERROR);
      setSnackbarOpen(true);
      setFiles([]);
      setUploadProgress([]);
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files) {
      const newFiles = Array.from(event.target.files);
      await handleFiles(newFiles);
      // Allow re-selecting the same file after removal by resetting the input
      event.target.value = '';
    }
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);

    if (event.dataTransfer.files) {
      const newFiles = Array.from(event.dataTransfer.files);
      await handleFiles(newFiles);
    }
  };

  const handleDelete = async (file: File, uploadedFile?: IUserFileDB) => {
    const fileIndex = files.findIndex((f) => f === file);
    if (uploadedFile) {
      try {
        // Use uniqueKey for JSONB deletion (no more DB-generated IDs)
        const fileIdentifier = uploadedFile.uniqueKey;
        if (!fileIdentifier) {
          throw new Error('File uniqueKey is missing');
        }
        await deleteDocument(fileIdentifier).unwrap();
        setUploadedFiles((prevFiles) =>
          prevFiles.filter((f) => f.uniqueKey !== fileIdentifier)
        );
        setSnackbarMessage('File deleted successfully.');
        setSnackbarSeverity(SnackbarSeverityEnum.SUCCESS);
      } catch (error) {
        const errorMessage = errorHelper(error);
        setSnackbarMessage(errorMessage);
        setSnackbarSeverity(SnackbarSeverityEnum.ERROR);
      } finally {
        setSnackbarOpen(true);
      }
    }
    setFiles((prevFiles) => prevFiles.filter((f) => f !== file));
    if (fileIndex >= 0) {
      setUploadProgress((prev) =>
        prev.filter((_, index) => index !== fileIndex)
      );
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const triggerFileInput = () => {
    const fileInput = document.getElementById(
      ElementIdEnum.ID_CONFIRMATION_FILE_INPUT
    ) as HTMLInputElement | null;
    if (fileInput) {
      fileInput.click();
    }
  };

  return (
    <StepForm
      title={title}
      onNext={() => onSubmitFiles({ documents: files })}
      nextDisabled={isLoading || isDeleting || files.length === 0}
      backDisabled={isLoading || isDeleting}
      step={step}
      setStep={setStep}
      needBackButton={needBackButton}
    >
      <Box
        display={'flex'}
        flexDirection={'column'}
        justifyContent={'center'}
        alignItems={'center'}
        textAlign={'center'}
        gap={'36px'}
      >
        <Box
          style={{ width: '100%' }}
          display={'flex'}
          flexDirection={'column'}
          justifyContent={'center'}
          alignItems={'center'}
        >
          <Typography typography={'body3'}>{contentText}</Typography>
        </Box>
        <Box
          style={{ width: '100%' }}
          display={'flex'}
          flexDirection={'column'}
          gap={'36px'}
        >
          <DragAndDropBox
            handleDragOver={handleDragOver}
            handleDragLeave={handleDragLeave}
            handleDrop={handleDrop}
            handleFileChange={handleFileChange}
            triggerFileInput={triggerFileInput}
            fileInputId={ElementIdEnum.ID_CONFIRMATION_FILE_INPUT}
            text={dragAndDropContentText}
            icon={<UploadIcon />}
          />
          {files.length > 0 && (
            <Box
              style={{ width: '100%' }}
              display={'flex'}
              flexDirection={'column'}
              gap={'36px'}
            >
              <Typography
                display={'flex'}
                variant={'h3'}
                justifyContent={'flex-start'}
              >
                Uploaded documents
              </Typography>
              <UploadDocumentFileList
                uploadedFiles={uploadedFiles}
                setUploadedFiles={setUploadedFiles}
                files={files}
                setFiles={setFiles}
                isLoading={isLoading || isDeleting}
                handleDelete={handleDelete}
                uploadProgress={uploadProgress}
              />
            </Box>
          )}
        </Box>
      </Box>

      <SnackbarCustom
        open={snackbarOpen}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </StepForm>
  );
};

export default UploadDocument;
